import { useEffect, useState } from 'react';
import type {
  ErrorDisplayProps,
  NotFoundErrorPageProps,
} from '@backstage/frontend-plugin-api';
import {
  Accordion,
  AccordionPanel,
  AccordionTrigger,
  Alert,
  Box,
  Button,
  ButtonLink,
  Container,
  Flex,
  Text,
} from '@backstage/ui';
import './Fallbacks.css';

/**
 * The loading bar. Shown only once the wait passes 250 ms — the delay
 * core-components' `Progress` takes from the MUI theme — so a fast load does
 * not flash it.
 */
export function Progress() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 250);
    return () => clearTimeout(timer);
  }, []);
  if (!visible) return <span hidden data-testid="progress" />;
  return (
    <div
      role="progressbar"
      aria-label="Загрузка"
      data-testid="progress"
      className="atlas-progress"
    />
  );
}

/** A route nothing claims. `children` is what a caller adds below the text. */
export function NotFoundPage({ children }: NotFoundErrorPageProps) {
  return (
    <Container>
      <Flex
        className="atlas-not-found"
        direction="column"
        align="center"
        justify="center"
        gap="4"
      >
        <span className="atlas-not-found__code">404</span>
        <Text as="h1" variant="title-medium">
          Страница не найдена
        </Text>
        <Text color="secondary">
          Такой страницы нет — возможно, ссылка устарела или сущность удалили из
          каталога.
        </Text>
        <ButtonLink href="/" variant="primary">
          В каталог
        </ButtonLink>
        {children}
      </Flex>
    </Container>
  );
}

/**
 * What an extension that threw renders instead of itself: the message, the
 * stack folded away, and a retry that remounts the extension.
 */
export function ErrorDisplay({ plugin, error, resetError }: ErrorDisplayProps) {
  return (
    <Box p="4">
      <Alert
        status="danger"
        icon
        title={plugin ? `Ошибка в плагине ${plugin.id}` : 'Ошибка'}
        description={error.message}
        customActions={
          <Button variant="secondary" size="small" onPress={resetError}>
            Повторить
          </Button>
        }
      />
      {error.stack && (
        <Accordion>
          <AccordionTrigger title="Подробности" />
          <AccordionPanel>
            <pre className="atlas-error-stack">{error.stack}</pre>
          </AccordionPanel>
        </Accordion>
      )}
    </Box>
  );
}
